import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';
import { Image } from '../utils/interfaces/types';

interface HomeContextValue {
  images: Image[];
  isLoading: boolean;
  error: string | null;
  loadImages: () => Promise<void>;
}

const HomeContext = createContext<HomeContextValue | undefined>(undefined);

export function HomeProvider({ children }: { children: ReactNode }) {
  const [images, setImages] = useState<Image[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPromiseRef = useRef<Promise<void> | null>(null);

  const loadImages = useCallback(async (): Promise<void> => {
    if (fetchPromiseRef.current) {
      return fetchPromiseRef.current;
    }

    const promise = (async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('Home')
          .select('*')
          .order('order', { ascending: true });

        if (fetchError) {
          setError(fetchError.message);
          fetchPromiseRef.current = null; // permite reintentar si falló
          return;
        }

        setImages((data ?? []) as Image[]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido');
        fetchPromiseRef.current = null; // permite reintentar si falló
      } finally {
        setIsLoading(false);
      }
    })();

    fetchPromiseRef.current = promise;
    return promise;
  }, []);

  return (
    <HomeContext.Provider value={{ images, isLoading, error, loadImages }}>
      {children}
    </HomeContext.Provider>
  );
}

export function useHome() {
  const context = useContext(HomeContext);
  if (!context) {
    throw new Error('useProjects debe usarse dentro de <ProjectsProvider>');
  }
  return context;
}
